import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import DoctorService from "../../services/doctorService";
import Seo from "../../seo/Seo";
import { useRecaptchaToken, RECAPTCHA_ACTIONS } from "../../utils/recaptcha";

export default function IndividualLogin({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { t } = useTranslation('login');
  const { t: tSeo } = useTranslation('seo');
  const doctorService = new DoctorService();
  const getRecaptchaToken = useRecaptchaToken();
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Frontend validation
    if (!formData.email || !formData.email.trim()) {
      setError(t('individual.validation.emailRequired'));
      return;
    }
    
    if (!formData.password || !formData.password.trim()) {
      setError(t('individual.validation.passwordRequired'));
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('individual.validation.emailInvalid'));
      return;
    }
    
    try {
      const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.DOCTOR_LOGIN);
      const response = await doctorService.doctorLogin({
        ...formData,
        recaptchaToken,
        recaptchaAction: RECAPTCHA_ACTIONS.DOCTOR_LOGIN,
      });
      
      // Token ve doctor bilgilerini localStorage'a kaydet
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("doctorId", response.data.doctor.id);
      localStorage.setItem("user", JSON.stringify({
        id: response.data.doctor.id,
        email: response.data.doctor.email,
        role: response.data.doctor.role,
        tenant_id: response.data.doctor.tenant_id,
        name: response.data.doctor.name,
        surname: response.data.doctor.surname,
        nationality: response.data.doctor.nationality
      }));
      
      // Nationality'ye göre dil ayarla (yeni giriş yapıldığı için force=true)
      if (response.data.doctor.nationality) {
        const { setLanguageFromNationality } = await import("../../utils/languageFromNationality");
        setLanguageFromNationality(response.data.doctor.nationality, true);
      }
      
      setOpenSnackBar(true);
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || t('individual.validation.loginError'));
    }
  };

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[500] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <Seo
        path="/individual-login"
        title={tSeo("individualLogin.title")}
        description={tSeo("individualLogin.description")}
        keywords={tSeo("individualLogin.keywords")}
        noindex
      />
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          display: "flex",
          flexDirection: "column",
          mt: "64px",
        }}
      >
        <Container maxWidth="sm" sx={{ py: 8, flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: "24px",
                background: darkMode ? colors.primary[400] : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: darkMode ? 'white' : colors.grey[100],
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  mb: 4,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t('individual.title')}
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
                    {t('individual.emailLabel')}
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    name="email"
                    type="email"
                    value={formData.email}
                    placeholder={t('individual.emailPlaceholder')}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.greenAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
                    {t('individual.passwordLabel')}
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t('individual.passwordPlaceholder')}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOffIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            ) : (
                              <VisibilityIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.greenAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.greenAccent[600]})`,
                    color: "white",
                    borderRadius: "12px",
                    padding: "12px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${colors.greenAccent[600]}, ${colors.greenAccent[700]})`,
                    },
                  }}
                >
                  {t('individual.loginButton')}
                </Button>

                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      cursor: "pointer",
                      color: colors.blueAccent[400],
                      fontWeight: 500,
                      "&:hover": {
                        textDecoration: "underline",
                        color: colors.blueAccent[300],
                      },
                    }}
                    onClick={() => navigate("/forgot-password/doctor")}
                  >
                    {t('individual.forgotPassword')}
                  </Typography>
                </Box>

                <Typography
                  sx={{ textAlign: "center", mt: 3, color: colors.grey[500] }}
                  variant="body1"
                >
                  {t('individual.noAccount')}{" "}
                  <span
                    style={{
                      cursor: "pointer",
                      color: colors.greenAccent[500],
                      fontWeight: 600,
                    }}
                    onClick={() => navigate("/tenantRegister")}
                  >
                    {t('individual.signUp')}
                  </span>
                </Typography>
              </form>
            </Paper>
          </motion.div>
        </Container>

        <Footer />

        <Snackbar
          open={openSnackBar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackBar(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={() => setOpenSnackBar(false)}
            severity="success"
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: "12px",
              backgroundColor: colors.greenAccent[500],
            }}
          >
            <Typography variant="body1" color="white">
              {t('individual.success')}
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
